import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import MyLeavePanel from '../components/MyLeavePanel';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import './Settings.css';
import './MySalaryLeave.css';

function getAuthToken() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return '';
    const u = JSON.parse(raw);
    return String(u?.token || '').trim();
  } catch { return ''; }
}

function fmtWon(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return `${x.toLocaleString('ko-KR')}원`;
}

function visibleLineItems(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.filter((it) => !it || !it.hidden);
}

function ymLabelKo(ym) {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split('-');
  return `${y}년 ${Number(m)}월`;
}

function useCalendarBounds() {
  return useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    return {
      currentYear: y,
      currentMonth: m,
      minYear: Math.min(2000, y - 25),
      maxYear: y,
    };
  }, []);
}

function isMonthInFuture(year, month, bounds) {
  if (year > bounds.currentYear) return true;
  if (year < bounds.currentYear) return false;
  return month > bounds.currentMonth;
}

function ymFromParts(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

const ROWS_TWO_COL_THRESHOLD = 7;

function splitHalfForTwoColGrid(items) {
  if (!items.length) return [[], []];
  const mid = Math.ceil(items.length / 2);
  return [items.slice(0, mid), items.slice(mid)];
}

/** column-count 대신 2개의 ul 그리드로 나눔 — flex 행 폭 붕괴(세로 한 글자) 방지 */
function SalaryItemRows({ items, rowKeyPrefix }) {
  const cols2 = items.length >= ROWS_TWO_COL_THRESHOLD;
  const renderRow = (row, i, keyP) => (
    <li key={`${keyP}-${i}-${row.name}`} className="salary-doc-row">
      <span className="salary-doc-row__name">{row.name || '—'}</span>
      <span className="salary-doc-row__amt">{fmtWon(row.amount)}</span>
    </li>
  );

  if (!cols2) {
    return <ul className="salary-doc-rows">{items.map((row, i) => renderRow(row, i, rowKeyPrefix))}</ul>;
  }
  const [left, right] = splitHalfForTwoColGrid(items);
  return (
    <div className="salary-doc-rows-grid">
      <ul className="salary-doc-rows salary-doc-rows--col">{left.map((row, i) => renderRow(row, i, `${rowKeyPrefix}L`))}</ul>
      <ul className="salary-doc-rows salary-doc-rows--col">{right.map((row, i) => renderRow(row, i, `${rowKeyPrefix}R`))}</ul>
    </div>
  );
}

function fmtMd(d) {
  const [, m, day] = String(d || '').split('-');
  return m ? `${Number(m)}/${Number(day)}` : d;
}

function placeLabel(addr, km) {
  if (km != null && km < 1) return '사무실';
  const a = String(addr || '').trim();
  if (a) return a;
  return km != null ? `사무실에서 ${km}km` : '';
}

/** 급여명세서 하단 — 해당 월 근태 이력 + 출장·특근 정산 */
function PayslipAttendanceSection({ summary, loading, error }) {
  if (loading) return <div className="salary-att-empty">근태 이력을 불러오는 중…</div>;
  if (error) return <div className="salary-att-empty">{error}</div>;
  if (!summary) return null;
  const days = summary.days || [];
  const allowances = (summary.allowances || []).filter((a) => a.count > 0);
  const trips = summary.trip?.trips || [];
  const hasSettlement = allowances.length > 0 || trips.length > 0;

  return (
    <div className="salary-att">
      {hasSettlement ? (
        <section className="salary-doc-block salary-att-block" aria-label="출장·특근 정산">
          <div className="salary-doc-block__head salary-att-head">
            <span>출장 · 특근 정산</span>
            <span className="salary-att-head__sum">합계 {fmtWon(summary.grand_total)}</span>
          </div>
          <div className="salary-att-settle">
            <table className="salary-att-table salary-att-table--settle">
              <thead>
                <tr>
                  <th>구분</th>
                  <th>항목</th>
                  <th className="r">횟수</th>
                  <th className="r">단가</th>
                  <th className="r">금액</th>
                  <th>발생일</th>
                </tr>
              </thead>
              <tbody>
                {allowances.map((a) => (
                  <tr key={a.key}>
                    <td className="att-s-kind">특근수당</td>
                    <td className="att-s-item">{a.label}</td>
                    <td className="r att-s-cnt">{a.count}회</td>
                    <td className="r att-s-unit">{a.unit != null ? fmtWon(a.unit) : '미설정'}</td>
                    <td className="r b att-s-amt">{fmtWon(a.amount)}</td>
                    <td className="salary-att-dates att-s-dates">{a.dates.map(fmtMd).join(', ')}</td>
                  </tr>
                ))}
                {summary.trip?.meal ? (
                  <tr>
                    <td className="att-s-kind">출장비</td>
                    <td className="att-s-item">식대 · 간식</td>
                    <td className="r att-s-cnt">{trips.filter((t) => Number(t.effective_meal || 0)).length}일</td>
                    <td className="r att-s-unit att-s-unit--none">—</td>
                    <td className="r b att-s-amt">{fmtWon(summary.trip.meal)}</td>
                    <td className="salary-att-dates att-s-dates">
                      {trips.filter((t) => Number(t.effective_meal || 0)).map((t) => fmtMd(t.date)).join(', ')}
                    </td>
                  </tr>
                ) : null}
                {summary.trip?.lodging ? (
                  <tr>
                    <td className="att-s-kind">출장비</td>
                    <td className="att-s-item">숙박</td>
                    <td className="r att-s-cnt">{trips.filter((t) => Number(t.effective_lodging || 0)).length}박</td>
                    <td className="r att-s-unit att-s-unit--none">—</td>
                    <td className="r b att-s-amt">{fmtWon(summary.trip.lodging)}</td>
                    <td className="salary-att-dates att-s-dates">
                      {trips.filter((t) => Number(t.effective_lodging || 0)).map((t) => fmtMd(t.date)).join(', ')}
                    </td>
                  </tr>
                ) : null}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} className="att-s-foot-label">특근수당 {fmtWon(summary.allowance_total)} + 출장비 {fmtWon(summary.trip?.total || 0)}</td>
                  <td className="r b att-s-foot-amt">{fmtWon(summary.grand_total)}</td>
                  <td className="att-s-foot-pad" />
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      ) : null}

      <section className="salary-doc-block salary-att-block" aria-label="근태 이력">
        <div className="salary-doc-block__head salary-att-head">
          <span>근태 이력</span>
          <span className="salary-att-head__sum">출근 {summary.work_days}일</span>
        </div>
        {days.length === 0 ? (
          <div className="salary-doc-empty">해당 월 근태 기록이 없습니다.</div>
        ) : (
          <table className="salary-att-table salary-att-table--days">
            <thead>
              <tr>
                <th>일자</th>
                <th>출근</th>
                <th>출근 위치</th>
                <th>퇴근</th>
                <th>퇴근 위치</th>
                <th>수당 · 출장</th>
                <th className="r">출장비</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.date} className={d.weekday === '토' || d.weekday === '일' ? 'is-weekend' : ''}>
                  <td className="nowrap att-d-date">{fmtMd(d.date)} ({d.weekday})</td>
                  <td className="nowrap b att-d-go">{d.go || '—'}</td>
                  <td className="salary-att-place att-d-goplace" title={d.go_addr || ''}>{placeLabel(d.go_addr, d.go_km)}</td>
                  <td className="nowrap b att-d-leave">{d.leave || '—'}</td>
                  <td className="salary-att-place att-d-leaveplace" title={d.leave_addr || ''}>{placeLabel(d.leave_addr, d.leave_km)}</td>
                  <td className={`att-d-tags${d.tags.length || d.trip_label || d.pending || d.trip_start || d.trip_end ? '' : ' is-empty'}`}>
                    {d.trip_start || d.trip_end ? (
                      <span className="salary-att-tag">🚗 출장 {d.trip_start || '--:--'}~{d.trip_end || '--:--'}</span>
                    ) : null}
                    {d.tags.map((t) => <span key={t} className="salary-att-tag">{t}</span>)}
                    {d.trip_label ? <span className="salary-att-trip">{d.trip_label}</span> : null}
                    {d.pending ? <span className="salary-att-tag salary-att-tag--pending">승인대기</span> : null}
                  </td>
                  <td className="r nowrap att-d-amt">{d.trip_amount ? fmtWon(d.trip_amount) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function PayslipDocument({ yearMonth, loading, error, record, onClose, attendance }) {
  const allowances = visibleLineItems(record?.allowances);
  const deductions = visibleLineItems(record?.deductions);

  return (
    <div
      className="salary-doc-backdrop salary-hide-scrollbar"
      role="presentation"
      onClick={onClose}
    >
      <div className="salary-doc-shell" role="dialog" aria-modal="true" aria-labelledby="salary-doc-title">
        <button type="button" className="salary-doc-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <div className="salary-doc-paper" onClick={(e) => e.stopPropagation()}>
          <header className="salary-doc-header">
            <span className="salary-doc-header__ribbon">Payroll Statement</span>
            <div className="salary-doc-header__top">
              <div className="salary-doc-header__titles">
                <h1 id="salary-doc-title" className="salary-doc-header__title">
                  <span className="salary-doc-header__title-main">급여 명세서</span>
                  {record && !loading && !error && String(record.emp_name || '').trim() ? (
                    <span className="salary-doc-header__title-emp">
                      {String(record.emp_name).trim()} 님
                    </span>
                  ) : null}
                </h1>
              </div>
              {record && !loading && !error ? (
                <div className="salary-doc-header__sums" aria-label="급여 합계">
                  <div className="salary-doc-header__sum">
                    <span className="salary-doc-header__sum-label">지급 합계</span>
                    <span className="salary-doc-header__sum-value">{fmtWon(record.total_allow)}</span>
                  </div>
                  <div className="salary-doc-header__sum">
                    <span className="salary-doc-header__sum-label">공제 합계</span>
                    <span className="salary-doc-header__sum-value">{fmtWon(record.total_deduct)}</span>
                  </div>
                  <div className="salary-doc-header__sum salary-doc-header__sum--net">
                    <span className="salary-doc-header__sum-label">실수령액</span>
                    <span className="salary-doc-header__sum-value">{fmtWon(record.net_pay)}</span>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="salary-doc-header__sub">{ymLabelKo(yearMonth)} 귀속</div>
          </header>

          {loading ? (
            <div className="salary-doc-loading">명세서를 불러오는 중…</div>
          ) : error ? (
            <div className="salary-doc-error">{error}</div>
          ) : !record ? (
            <div className="salary-doc-body salary-hide-scrollbar">
              <div className="salary-doc-loading" style={{ color: '#a8a29e' }}>
                해당 월에 등록된 급여 명세가 없습니다.
              </div>
              <PayslipAttendanceSection {...attendance} />
            </div>
          ) : (
            <>
              <div className="salary-doc-body salary-hide-scrollbar">
                <div className="salary-doc-columns">
                  <section className="salary-doc-block" aria-label="지급 내역">
                    <div className="salary-doc-block__head">지급</div>
                    {allowances.length === 0 ? (
                      <div className="salary-doc-empty">항목 없음</div>
                    ) : (
                      <SalaryItemRows items={allowances} rowKeyPrefix="a" />
                    )}
                  </section>

                  <section className="salary-doc-block" aria-label="공제 내역">
                    <div className="salary-doc-block__head">공제</div>
                    {deductions.length === 0 ? (
                      <div className="salary-doc-empty">항목 없음</div>
                    ) : (
                      <SalaryItemRows items={deductions} rowKeyPrefix="d" />
                    )}
                  </section>
                </div>

                {record.memo ? <div className="salary-doc-memo">비고 · {record.memo}</div> : null}
                <PayslipAttendanceSection {...attendance} />
                <p className="salary-doc-stamp">MRT</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export default function MySalaryLeave() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const bounds = useCalendarBounds();
  const [calendarYear, setCalendarYear] = useState(() => bounds.currentYear);
  const [docOpen, setDocOpen] = useState(false);
  const [docYm, setDocYm] = useState('');
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState('');
  const [docRecord, setDocRecord] = useState(null);
  const [attSummary, setAttSummary] = useState(null);
  const [attLoading, setAttLoading] = useState(false);
  const [attError, setAttError] = useState('');

  const apiBase = useMemo(() => String(getApiBaseUrl() || '').replace(/\/$/, ''), []);

  const canPrevYear = calendarYear > bounds.minYear;
  const canNextYear = calendarYear < bounds.maxYear;

  const fetchPayslip = useCallback(
    async (ym) => {
      if (!ym || ym.length !== 7) return;
      setDocLoading(true);
      setDocError('');
      setDocRecord(null);
      try {
        const q = new URLSearchParams({ year_month: ym });
        const url = `${apiBase}/api/erp_salary/history/mine?${q.toString()}`;
        const tok = getAuthToken();
        const res = await fetch(url, tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg = data?.detail || data?.message || res.statusText || '조회에 실패했습니다.';
          setDocError(typeof msg === 'string' ? msg : JSON.stringify(msg));
          return;
        }
        if (!data?.ok) {
          setDocError('급여 데이터를 불러오지 못했습니다.');
          return;
        }
        setDocRecord(data.record || null);
      } catch (e) {
        setDocError(e?.message || '네트워크 오류가 발생했습니다.');
      } finally {
        setDocLoading(false);
      }
    },
    [apiBase]
  );

  const fetchAttendance = useCallback(
    async (ym) => {
      setAttLoading(true);
      setAttError('');
      setAttSummary(null);
      try {
        const q = new URLSearchParams({ year_month: ym });
        const tok = getAuthToken();
        const res = await fetch(
          `${apiBase}/api/erp_attendance/month_summary?${q.toString()}`,
          tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined,
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.ok) {
          setAttError(data?.detail || '근태 이력을 불러오지 못했습니다.');
          return;
        }
        setAttSummary(data);
      } catch (e) {
        setAttError(e?.message || '근태 이력을 불러오지 못했습니다.');
      } finally {
        setAttLoading(false);
      }
    },
    [apiBase]
  );

  const openPayslip = useCallback(
    (ym) => {
      setDocYm(ym);
      setDocOpen(true);
      fetchPayslip(ym);
      fetchAttendance(ym);
    },
    [fetchPayslip, fetchAttendance]
  );

  const closePayslip = useCallback(() => {
    setDocOpen(false);
    setDocYm('');
    setDocError('');
    setDocRecord(null);
    setDocLoading(false);
    setAttSummary(null);
    setAttError('');
  }, []);

  useEffect(() => {
    if (!docOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closePayslip();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [docOpen, closePayslip]);

  return (
    <div className="settings-container salary-page">
      <Header leftActions={
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            height: '34px', padding: '0 12px',
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.40)',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: 900,
            fontSize: '13px',
            whiteSpace: 'nowrap',
          }}
        >
          ☰ 전체
        </button>
      } />
      <div className="settings-wrap">
        <div className="settings-head">
          <div className="settings-title">
            <h2>내 급여 및 연차 보기</h2>
            <div className="settings-sub">
              {user?.email ? `${user.email} · ` : ''}
              달력에서 귀속 월을 선택하면 명세서가 열립니다.
            </div>
          </div>
        </div>

        <div className="salary-leave-split">
          <div className="settings-card salary-leave-split__cal">
            <div className="settings-card-title">급여 명세서</div>
            <div className="salary-cal-panel">
              <p className="salary-cal-hint">
                연도를 바꾼 뒤 <strong>월</strong>을 누르세요. 미래 월은 선택할 수 없습니다. (ERP 등록분만 표시)
              </p>
              <div className="salary-cal-nav">
                <button
                  type="button"
                  className="salary-cal-nav__btn"
                  aria-label="이전 연도"
                  disabled={!canPrevYear}
                  onClick={() => canPrevYear && setCalendarYear((y) => y - 1)}
                >
                  ‹
                </button>
                <span className="salary-cal-nav__year" aria-live="polite">
                  {calendarYear}
                </span>
                <button
                  type="button"
                  className="salary-cal-nav__btn"
                  aria-label="다음 연도"
                  disabled={!canNextYear}
                  onClick={() => canNextYear && setCalendarYear((y) => y + 1)}
                >
                  ›
                </button>
              </div>
              <div className="salary-cal-grid">
                {MONTHS.map((month) => {
                  const ym = ymFromParts(calendarYear, month);
                  const future = isMonthInFuture(calendarYear, month, bounds);
                  const isTodayMonth =
                    calendarYear === bounds.currentYear && month === bounds.currentMonth;
                  return (
                    <button
                      key={ym}
                      type="button"
                      className={[
                        'salary-cal-cell',
                        future ? 'salary-cal-cell--future' : '',
                        isTodayMonth && !future ? 'salary-cal-cell--today' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      disabled={future}
                      onClick={() => !future && openPayslip(ym)}
                    >
                      <span className="salary-cal-cell__num">{month}</span>
                      <span className="salary-cal-cell__label">월</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="settings-card salary-leave-split__leave">
            <MyLeavePanel email={user?.email} displayName={user?.name || user?.username || ''} />
          </div>
        </div>
      </div>

      {docOpen ? (
        <PayslipDocument
          yearMonth={docYm}
          loading={docLoading}
          error={docError}
          record={docRecord}
          onClose={closePayslip}
          attendance={{ summary: attSummary, loading: attLoading, error: attError }}
        />
      ) : null}
    </div>
  );
}
